import { useCallback, useEffect, useMemo } from 'react';

import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useForm } from '@/components/react-hook-form';
import { useReportError } from '@/hooks/useReportError';
import {
  ModelMappingModelPurpose,
  OpenaiSdkConfigProvider,
} from '@/types/schema';

import {
  AzureByokIntegrationCard_OpenaiSdkConfigFragment,
  Operations,
  useCreateOpenaiSdkConfigMutation,
  useUpdateOpenaiSdkConfigMutation,
} from '../../graphql/AdminIntegrationsPage.generated';

const MODEL_PURPOSES = Object.values(ModelMappingModelPurpose);

export interface AzureBYOKFormShape {
  baseURL: string;
  apiKey: string;
  models: Record<ModelMappingModelPurpose, string>;
}

interface UseAzureBYOKFormProps {
  azureConfig: AzureByokIntegrationCard_OpenaiSdkConfigFragment | null;
}

export function useAzureBYOKForm({ azureConfig }: UseAzureBYOKFormProps) {
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const [createOpenaiSdkConfig, { loading: createConfigLoading }] =
    useCreateOpenaiSdkConfigMutation({
      onError: (err) => {
        showFeedback('Failed to create Azure configuration');
        reportError('Failed to create Azure configuration', err);
      },
    });

  const [updateOpenaiSdkConfig, { loading: updateConfigLoading }] =
    useUpdateOpenaiSdkConfigMutation({
      onError: (err) => {
        showFeedback('Failed to update Azure configuration');
        reportError('Failed to update Azure configuration', err);
      },
    });

  const loading = createConfigLoading || updateConfigLoading;

  const existingModelMappings = useMemo(() => {
    const mappings: Partial<
      Record<ModelMappingModelPurpose, { id: string; modelName: string }>
    > = {};
    azureConfig?.modelMappings?.edges?.forEach((edge) => {
      if (edge?.node) {
        mappings[edge.node.modelPurpose] = {
          id: edge.node.id,
          modelName: edge.node.modelName,
        };
      }
    });
    return mappings;
  }, [azureConfig]);

  const getDefaultValues = useCallback((): AzureBYOKFormShape => {
    const models = {} as Record<ModelMappingModelPurpose, string>;
    MODEL_PURPOSES.forEach((purpose) => {
      models[purpose] = existingModelMappings[purpose]?.modelName ?? '';
    });
    return {
      baseURL: azureConfig?.baseURL ?? '',
      apiKey: '',
      models,
    };
  }, [azureConfig?.baseURL, existingModelMappings]);

  const formMethods = useForm<AzureBYOKFormShape>({
    defaultValues: getDefaultValues(),
    mode: 'onChange',
  });

  const { reset } = formMethods;

  useEffect(() => {
    reset(getDefaultValues());
  }, [azureConfig, existingModelMappings, getDefaultValues, reset]);

  const handleToggleEnabled = useCallback(
    async (enabled: boolean) => {
      if (!azureConfig) return;

      await updateOpenaiSdkConfig({
        variables: {
          input: {
            id: azureConfig.id,
            update: {
              enabled,
            },
          },
        },
        refetchQueries: [Operations.Query.GetIntegrationConfiguration],
        awaitRefetchQueries: true,
        onCompleted: () => {
          showFeedback(
            enabled ? 'Azure provider enabled' : 'Azure provider disabled',
            { variant: 'success' }
          );
        },
      });
    },
    [azureConfig, updateOpenaiSdkConfig, showFeedback]
  );

  const azureConfigId = azureConfig?.id;
  const onSubmit = useCallback(
    async (data: AzureBYOKFormShape) => {
      try {
        const withModelMappings = MODEL_PURPOSES.filter(
          (purpose) => data.models[purpose] && !existingModelMappings[purpose]
        ).map((purpose) => ({
          create: {
            modelPurpose: purpose,
            modelName: data.models[purpose],
          },
        }));

        const updateModelMappings = MODEL_PURPOSES.filter((purpose) => {
          const existing = existingModelMappings[purpose];
          return existing && data.models[purpose] !== existing.modelName;
        }).map((purpose) => ({
          id: existingModelMappings[purpose]!.id,
          update: {
            modelName: data.models[purpose],
          },
        }));

        if (!azureConfigId) {
          const response = await createOpenaiSdkConfig({
            variables: {
              input: {
                create: {
                  provider: OpenaiSdkConfigProvider.Azure,
                  baseURL: data.baseURL || undefined,
                  apiKey: data.apiKey,
                  enabled: true,
                },
                withModelMappings,
              },
            },
            refetchQueries: [Operations.Query.GetIntegrationConfiguration],
            awaitRefetchQueries: true,
          });

          if (!response.data?.createOpenaiSdkConfig) return;
        } else {
          const response = await updateOpenaiSdkConfig({
            variables: {
              input: {
                id: azureConfigId,
                update: {
                  baseURL: data.baseURL || undefined,
                  clearBaseURL: !data.baseURL,
                  enabled: true,
                  apiKey: data.apiKey || undefined,
                },
                withModelMappings,
                updateModelMappings,
              },
            },
            refetchQueries: [Operations.Query.GetIntegrationConfiguration],
            awaitRefetchQueries: true,
          });

          if (!response.data?.updateOpenaiSdkConfig) return;
        }

        showFeedback('Azure configuration updated successfully', {
          variant: 'success',
        });
      } catch (err) {
        reportError(
          'Failed to update Azure configuration',
          err instanceof Error ? err : null
        );
      }
    },
    [
      azureConfigId,
      createOpenaiSdkConfig,
      updateOpenaiSdkConfig,
      existingModelMappings,
      showFeedback,
      reportError,
    ]
  );

  return {
    formMethods,
    loading,
    onSubmit,
    handleToggleEnabled,
  };
}
